import React, { ReactNode, useState, useEffect } from 'react'
import { createPortal } from 'react-dom'
import FocusTrap from 'react-focus-lock'
import { use100vh } from 'react-div-100vh'

import { Box } from '../../Box/Box'

const ANIMATION_OPEN_TIMING = 30
const ANIMATION_CLOSE_TIMING = 80

const ANIMATIONS = {
  backdrop: {
    opacity: 0.4
  },
  dialog: {
    closedScale: 0.7,
    openScale: 1
  },
  drawer: {
    closedOffset: 40,
    openOffset: 0
  }
}

interface ModalPortalProps {
  children: ReactNode
}

const ModalPortal = ({ children }: ModalPortalProps) => {
  const [modalElement, setElement] = useState<HTMLElement | null>(null)

  useEffect(() => {
    const modalContainerId = 'pulsar-modal-container'
    let element = document.getElementById(modalContainerId)

    if (!element) {
      element = document.createElement('div')
      element.setAttribute('id', modalContainerId)
      element.setAttribute('class', '')

      document.body.appendChild(element)
    }

    setElement(element)
  }, [])

  if (!modalElement) {
    return null
  }

  return createPortal(<>{children}</>, modalElement)
}

export interface ModalProps {
  children: ReactNode
  open: boolean
  onClose: () => void
  position: 'center' | 'left' | 'right'
  closeOnClick?: boolean
}

export const Modal: React.FC<ModalProps> = ({
  open,
  onClose,
  position,
  closeOnClick = true,
  children
}) => {
  const [modalOpen, setModalOpen] = useState(false)

  const closeModal = () => {
    setModalOpacity(0)
    setBackdropOpacity(0)
    setScale(ANIMATIONS.dialog.closedScale)
    setTranslate(ANIMATIONS.drawer.closedOffset)
    setTimeout(() => setModalOpen(false), ANIMATION_CLOSE_TIMING)
    onClose()
  }

  const openModal = () => {
    setModalOpen(true)
    setTimeout(() => {
      setBackdropOpacity(ANIMATIONS.backdrop.opacity)
      setModalOpacity(1)
      setScale(1)
      setTranslate(0)
    }, ANIMATION_OPEN_TIMING)
  }

  useEffect(() => {
    if (open) openModal()
    else closeModal()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const handleEscape = (event: React.KeyboardEvent<HTMLElement>) => {
    if (event.key === 'Escape' && closeOnClick) {
      event.stopPropagation()
      closeModal()
    }
  }

  const [backdropOpacity, setBackdropOpacity] = useState(0)
  const [modalOpacity, setModalOpacity] = useState(0)
  const [scale, setScale] = useState(ANIMATIONS.dialog.closedScale)
  const [translate, setTranslate] = useState(ANIMATIONS.drawer.closedOffset)

  const modalTransform = {
    center: `scale(${scale})`,
    left: `translateX(-${translate}px)`,
    right: `translateX(${translate}px)`
  }

  const modalPosition = {
    center: 'center',
    left: 'start',
    right: 'end'
  }

  const viewPortHeight = use100vh()

  return (
    <ModalPortal>
      {modalOpen ? (
        <FocusTrap returnFocus>
          <Box
            onClick={modalOpen && closeOnClick ? closeModal : undefined}
            id="pulsar-modal--backdrop"
            position="fixed"
            background="var(--colors-modal--backdrop)"
            opacity={backdropOpacity}
            top={0}
            right={0}
            bottom={0}
            left={0}
            zIndex="var(--zindex-modal--backdrop)"
            transition={position === 'center' ? '80ms' : undefined}
          />
          <Box
            id="pulsar-modal"
            position="fixed"
            height={viewPortHeight ?? undefined}
            top={0}
            right={0}
            bottom={0}
            left={0}
            inset={position === 'center' ? 'm' : undefined}
            pointerEvents="none"
            zIndex="var(--zindex-modal)"
            opacity={modalOpacity}
            transform={modalTransform[position as keyof typeof modalTransform]}
            transition="80ms"
          >
            <Box
              role="dialog"
              aria-modal="true"
              id="pulsar-modal--content"
              height="100%"
              width="100%"
              position="relative"
              display="flex"
              justifyContent={
                modalPosition[position as keyof typeof modalPosition] as ResponsiveProp<any>
              }
              alignItems="center"
              onKeyDown={handleEscape}
            >
              {children}
            </Box>
          </Box>
        </FocusTrap>
      ) : null}
    </ModalPortal>
  )
}
